import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, Sparkles, Activity } from 'lucide-react';
import { api, relTime } from './api';
import { MaternalBackLink, MaternalDisclaimer } from './_ui';

/**
 * Maternal · Symptoms Tracker
 *
 * Read-only triage explainer + symptom history.
 *
 *   Top: a 3-band legend that explains green / amber / rose meaning so users
 *        understand how to read the chips on the daily check-in.
 *   Mid: 14-day symptom history pulled from /api/maternal/checkins/recent.
 *   Btm: a CTA back into the Daily Check-in (optionally pre-filling symptoms
 *        the user just tapped here).
 *
 * No POST happens from this screen — symptoms are recorded as part of the
 * daily check-in, never silently in the background.
 */

const COMMON = [
  'nausea', 'fatigue', 'tender_breasts', 'back_pain', 'heartburn',
  'constipation', 'headache_mild', 'swelling_mild', 'cramps_mild',
];
const MONITOR = ['nausea_severe', 'swelling_face_hands', 'heartburn_severe', 'anxiety', 'sleep_loss'];
const RED = [
  'bleeding', 'severe_headache', 'vision_changes', 'reduced_movement',
  'leaking_fluid', 'calf_pain', 'severe_abdominal_pain', 'fever_high',
];

const HUMAN = {
  nausea: 'Nausea', fatigue: 'Fatigue', tender_breasts: 'Tender breasts',
  back_pain: 'Back pain', heartburn: 'Heartburn', constipation: 'Constipation',
  headache_mild: 'Mild headache', swelling_mild: 'Mild swelling', cramps_mild: 'Mild cramps',
  nausea_severe: 'Severe nausea', swelling_face_hands: 'Face / hand swelling',
  heartburn_severe: 'Severe heartburn', anxiety: 'Anxiety', sleep_loss: 'Sleep loss',
  bleeding: 'Bleeding', severe_headache: 'Severe headache',
  vision_changes: 'Vision changes', reduced_movement: 'Reduced movement',
  leaking_fluid: 'Leaking fluid', calf_pain: 'Calf pain or swelling',
  severe_abdominal_pain: 'Severe abdominal pain', fever_high: 'Fever > 38°C',
};

const triageOf = (s) => {
  if (RED.includes(s)) return 'red';
  if (MONITOR.includes(s)) return 'amber';
  if (COMMON.includes(s)) return 'green';
  return 'gray';
};

const TRIAGE_TEXT = {
  green:  { title: 'Common',          message: "Often part of a normal pregnancy or recovery — gentle rest, hydration, and a balanced meal usually help." },
  amber:  { title: 'Worth monitoring', message: "Common, but worth watching. Hydrate, rest, and re-check tomorrow. If anything worsens, call your clinician." },
  red:    { title: 'Need care now',    message: "These signs aren't routine. Please call your midwife, GP, or maternity assessment unit today." },
};

const TONE_BG = {
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  red:   'bg-rose-50 border-rose-200 text-rose-800',
  gray:  'bg-gray-50 border-gray-200 text-gray-700',
};

const TONE_CHIP_ON = {
  green: 'bg-emerald-600 border-emerald-600 text-white',
  amber: 'bg-amber-500 border-amber-500 text-white',
  red:   'bg-rose-600 border-rose-600 text-white',
};
const TONE_CHIP = {
  green: 'bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50',
  amber: 'bg-white border-amber-200 text-amber-800 hover:bg-amber-50',
  red:   'bg-white border-rose-200 text-rose-700 hover:bg-rose-50',
};

const MaternalSymptoms = () => {
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    api
      .recentCheckins()
      .then((res) => setRecent(res?.data || []))
      .catch(() => setRecent([]))
      .finally(() => setLoading(false));
  }, []);

  const groupedRecent = useMemo(() => {
    return recent
      .map((r) => ({
        id: r.id,
        when: r.created_at,
        symptoms: (r.symptoms || []).filter((s) => s in HUMAN),
      }))
      .filter((r) => r.symptoms.length > 0);
  }, [recent]);

  // Highest triage in the picked set drives the live banner.
  const liveTriage = useMemo(() => {
    let level = null;
    for (const v of picked) {
      const t = triageOf(v);
      if (t === 'red') return 'red';
      if (t === 'amber' && level !== 'red') level = 'amber';
      if (t === 'green' && !level) level = 'green';
    }
    return level;
  }, [picked]);

  const togglePick = (v) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const goCheckinWithPicks = () => {
    const q = Array.from(picked);
    if (q.length === 0) {
      navigate('/maternal-health/checkin');
      return;
    }
    navigate(`/maternal-health/checkin?symptoms=${encodeURIComponent(q.join(','))}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-symptoms">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
              <ShieldAlert className="h-5 w-5 text-white" />
            </span>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Symptoms tracker</h1>
              <p className="text-xs text-gray-500 mt-0.5">A calm guide to what's common, what to monitor, and what needs care now.</p>
            </div>
          </div>
        </header>

        {/* Triage legend */}
        <section className="grid grid-cols-1 gap-2 mb-6" data-testid="maternal-symptoms-legend">
          {(['green','amber','red']).map((lvl) => (
            <div key={lvl} className={`rounded-2xl border-2 px-4 py-3 ${TONE_BG[lvl]}`}>
              <div className="flex items-start gap-2">
                {lvl === 'red' ? <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> : <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight">{TRIAGE_TEXT[lvl].title}</p>
                  <p className="text-xs leading-relaxed mt-0.5">{TRIAGE_TEXT[lvl].message}</p>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Quick picker */}
        <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-6" data-testid="maternal-symptoms-picker">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
            Tap what you're feeling
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Just exploring? Pick a few to see what they mean. To save them as part of your day, tap "Log a full check-in" below.
          </p>

          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1.5">Common</p>
              <div className="flex flex-wrap gap-1.5">
                {COMMON.map((v) => {
                  const on = picked.has(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => togglePick(v)}
                      className={`text-xs font-medium rounded-full px-3 py-1.5 border-2 transition-all ${on ? TONE_CHIP_ON.green : TONE_CHIP.green}`}
                      data-testid={`maternal-symptoms-pick-${v}`}
                      aria-pressed={on}
                    >
                      {HUMAN[v]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wider mb-1.5">Worth monitoring</p>
              <div className="flex flex-wrap gap-1.5">
                {MONITOR.map((v) => {
                  const on = picked.has(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => togglePick(v)}
                      className={`text-xs font-medium rounded-full px-3 py-1.5 border-2 transition-all ${on ? TONE_CHIP_ON.amber : TONE_CHIP.amber}`}
                      data-testid={`maternal-symptoms-pick-${v}`}
                      aria-pressed={on}
                    >
                      {HUMAN[v]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-rose-700 uppercase tracking-wider mb-1.5">Need care now</p>
              <div className="flex flex-wrap gap-1.5">
                {RED.map((v) => {
                  const on = picked.has(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => togglePick(v)}
                      className={`text-xs font-medium rounded-full px-3 py-1.5 border-2 transition-all ${on ? TONE_CHIP_ON.red : TONE_CHIP.red}`}
                      data-testid={`maternal-symptoms-pick-${v}`}
                      aria-pressed={on}
                    >
                      {HUMAN[v]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Live triage banner — driven by the highest level the user picked */}
          {liveTriage && (
            <div className={`mt-4 rounded-xl border-2 px-4 py-3 ${TONE_BG[liveTriage]}`} data-testid="maternal-symptoms-live-triage">
              <p className="text-sm font-bold leading-tight">{TRIAGE_TEXT[liveTriage].title}</p>
              <p className="text-xs leading-relaxed mt-0.5">{TRIAGE_TEXT[liveTriage].message}</p>
            </div>
          )}

          <button
            type="button"
            onClick={goCheckinWithPicks}
            className="mt-4 w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl px-5 py-3 transition-all"
            data-testid="maternal-symptoms-cta"
          >
            {picked.size > 0 ? `Log full check-in with ${picked.size} symptom${picked.size === 1 ? '' : 's'}` : 'Log a full check-in'}
            <span className="ml-1" aria-hidden="true">→</span>
          </button>
        </section>

        {/* Recent symptom history */}
        <section data-testid="maternal-symptoms-history">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 px-1">
            Recent symptoms (last 14 days)
          </p>
          {loading ? (
            <p className="text-sm text-gray-500 italic px-1">Loading your history…</p>
          ) : groupedRecent.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-5 text-center">
              <Activity className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-700 font-semibold">Nothing logged yet</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                Once you start logging symptoms in the daily check-in, they'll appear here so you can spot patterns.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {groupedRecent.map((row) => (
                <li
                  key={row.id}
                  className="rounded-2xl bg-white border border-gray-100 shadow-sm p-3"
                  data-testid={`maternal-symptoms-history-row-${row.id}`}
                >
                  <p className="text-[11px] font-semibold text-gray-500 mb-1.5">{relTime(row.when)}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {row.symptoms.map((s) => {
                      const t = triageOf(s);
                      const cls = t === 'red'
                        ? 'bg-rose-100 text-rose-800 border-rose-200'
                        : t === 'amber'
                        ? 'bg-amber-100 text-amber-900 border-amber-200'
                        : 'bg-emerald-100 text-emerald-800 border-emerald-200';
                      return (
                        <span
                          key={s}
                          className={`inline-flex items-center text-[11px] font-medium rounded-md px-2 py-0.5 border ${cls}`}
                        >
                          {HUMAN[s] || s}
                        </span>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalSymptoms;
